import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Stack,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  convertMagnifiedToDecimal,
  STOCK_UI,
} from '@diamond/shared/decimal-util-web';
import { Cart } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  getItemCardTrackingParams,
  isEven,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import {
  useCartDeleteItem,
  useCartItemPromoPrice,
  useStockNumberInput,
  useUpdateCartItem,
} from '@diamond/sol-buyer/utils';
import { useCartItemNote, useCartStore } from '@diamond/sol-buyer-context';
import Link from 'next/link';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ArrowRightIcon, DeleteIcon } from '../Icons';
import { QuantityControl } from '../QuantityControl';
import { CartItemImage, CartItemInfo } from './CartItemDisplay';
import { CartItemNoteEditor } from './CartItemNoteEditor';
import { CartItemNoteToggle } from './CartItemNoteToggle';
import { CartItemPromoPrice } from './CartItemPromoPrice';

const DEFAULT_FALLBACK_MAGNIFIED_QUANTITY = 1000;

/**
 * Converts a magnified stock quantity (integer, typically *1000) to the UI decimal quantity.
 * Guarantees a finite return value to avoid rendering `"NaN"` in quantity inputs.
 */
const toSafeUiStockQuantity = (
  magnifiedQty: number,
  fallbackMagnifiedQty: number
): number => {
  const uiQty = convertMagnifiedToDecimal(magnifiedQty, STOCK_UI);
  if (Number.isFinite(uiQty)) return uiQty;
  return convertMagnifiedToDecimal(fallbackMagnifiedQty, STOCK_UI);
};

export const CartItemActions = ({
  item,
  notify,
  onQuantityChange,
  isSelected,
  onLatestQuantityRefReady,
  noteForPatch,
}: {
  item: Cart;
  notify?: (type: 'success' | 'error', message: string) => void;
  onQuantityChange?: (magnifiedQuantity: number) => void;
  isSelected: boolean;
  onLatestQuantityRefReady?: (ref: React.MutableRefObject<number>) => void;
  /**
   * The latest note to include in quantity PATCHes. This prevents overwriting notes with stale values
   * because the backend contract requires `{ quantity, note }` together.
   */
  noteForPatch?: string;
}) => {
  const fallbackMagnifiedQuantity =
    item.product.minimum_order_quantity ?? DEFAULT_FALLBACK_MAGNIFIED_QUANTITY;

  const [uiQuantity, setUiQuantity] = useState<number>(() =>
    toSafeUiStockQuantity(item.quantity, fallbackMagnifiedQuantity)
  );
  const latestQuantityRef = useRef<number>(item.quantity);
  const quantityChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toast = useToast();
  const updateCartItemQuantity = useCartStore((s) => s.updateCartItemQuantity);

  const handleError = useCallback(
    (errorMessage: string) => {
      showToast(toast, 'error', errorMessage, undefined, 'bottom', 3000);
      setUiQuantity(
        toSafeUiStockQuantity(item.quantity, fallbackMagnifiedQuantity)
      );
      latestQuantityRef.current = item.quantity;

      if (isSelected) {
        updateCartItemQuantity(item.id, item.quantity);
      }
    },
    [
      toast,
      item.quantity,
      item.id,
      isSelected,
      updateCartItemQuantity,
      fallbackMagnifiedQuantity,
    ]
  );

  const { handleUpdateQuantity } = useUpdateCartItem({
    itemId: item.id,
    onError: handleError,
  });
  const { handleDeleteItem, isDeleting } = useCartDeleteItem({
    itemId: item.id,
    item,
    notify,
  });

  useEffect(() => {
    setUiQuantity(
      toSafeUiStockQuantity(item.quantity, fallbackMagnifiedQuantity)
    );
    latestQuantityRef.current = item.quantity;
  }, [item.quantity, item.id, fallbackMagnifiedQuantity]);

  useEffect(() => {
    onLatestQuantityRefReady?.(latestQuantityRef);
  }, []);

  const orderQtyMultiple = convertMagnifiedToDecimal(
    item.product.order_quantity_multiple,
    STOCK_UI
  );

  const minOrderQty = convertMagnifiedToDecimal(
    item.product.minimum_order_quantity,
    STOCK_UI
  );

  const productStock = convertMagnifiedToDecimal(item.product.stock, STOCK_UI);

  const maxStockGanjil =
    Math.floor(productStock / orderQtyMultiple) * orderQtyMultiple;

  const maxStockGenap =
    Math.round(productStock / orderQtyMultiple) * orderQtyMultiple;

  const debounceCallback = useCallback(
    (callback: () => void, delay: number) => {
      if (quantityChangeTimeoutRef.current) {
        clearTimeout(quantityChangeTimeoutRef.current);
      }
      quantityChangeTimeoutRef.current = setTimeout(() => {
        callback();
        quantityChangeTimeoutRef.current = null;
      }, delay);
    },
    []
  );

  /**
   * Reflects in-progress quantity edits in the UI (and selection state) without persisting them yet.
   */
  const previewQuantityChange = useCallback(
    (magnifiedQty: number) => {
      setUiQuantity(
        toSafeUiStockQuantity(magnifiedQty, fallbackMagnifiedQuantity)
      );
      latestQuantityRef.current = magnifiedQty;

      if (isSelected) {
        updateCartItemQuantity(item.id, magnifiedQty);
      }
    },
    [isSelected, item.id, updateCartItemQuantity, fallbackMagnifiedQuantity]
  );

  /**
   * Commits the final magnified quantity to the backend/store and notifies dependents.
   */
  const commitQuantityChange = useCallback(
    (magnifiedQty: number) => {
      handleUpdateQuantity(magnifiedQty, noteForPatch ?? item.note ?? '');

      if (isSelected) {
        updateCartItemQuantity(item.id, magnifiedQty);
      }

      debounceCallback(() => onQuantityChange?.(magnifiedQty), 300);
    },
    [
      handleUpdateQuantity,
      onQuantityChange,
      debounceCallback,
      isSelected,
      item.id,
      item.note,
      noteForPatch,
      updateCartItemQuantity,
    ]
  );

  const {
    hasFractionalStep: fractionalStep,
    inputProps,
    incProps,
    decProps,
  } = useStockNumberInput({
    id: `input-${item.id}`,
    name: `input-${item.id}`,
    valueUi: uiQuantity,
    stepUi: orderQtyMultiple,
    minUi: minOrderQty,
    maxUi: isEven(orderQtyMultiple) ? maxStockGanjil : maxStockGenap,
    stepMagnified: item.product.order_quantity_multiple,
    isDisabled:
      minOrderQty > productStock ||
      item.product.stock_status === 'OUT_OF_STOCK',
    onChangeMagnifiedImmediate: previewQuantityChange,
    onChangeMagnified: commitQuantityChange,
    onError: handleError,
  });

  useEffect(() => {
    return () => {
      if (quantityChangeTimeoutRef.current) {
        clearTimeout(quantityChangeTimeoutRef.current);
        quantityChangeTimeoutRef.current = null;
      }
    };
  }, []);

  const isProductEmpty = item.product.stock === 0;

  const nameUnit =
    item.product?.sales_unit_desc || item.product?.measurement_unit_desc;
  return (
    <VStack
      w={{ base: 'fit-content', lg: 'auto' }}
      gap={{ base: 2, lg: 0 }}
      id="cart-item-actions"
    >
      <Flex
        alignItems={{ base: 'center', lg: 'center' }}
        gap={{ base: '2', lg: '4' }}
        flexDirection={{ base: 'row', lg: 'row' }}
        w={{ base: '100%', lg: 'auto' }}
        justifyContent={{ base: 'space-between', lg: 'flex-start' }}
      >
        <IconButton
          name="remove-item"
          icon={<DeleteIcon />}
          aria-label="remove button"
          bg="#FAFAFA"
          color="red"
          size={{ base: '28px', lg: 'sm' }}
          _active={{ bgColor: 'white' }}
          _hover={{ bgColor: 'gray.33' }}
          onClick={handleDeleteItem}
          isLoading={isDeleting}
          display={{ base: 'none', lg: 'flex' }}
        />
        {isProductEmpty ? (
          <Button
            size={{ base: 'sm', lg: 'md' }}
            isDisabled
            colorScheme="gray"
            width={{ base: '100%', lg: '160px' }}
            color="gray.500"
            border="none"
            _disabled={{
              bg: '#D9D9D9',
              color: 'gray.500',
              cursor: 'not-allowed',
            }}
          >
            Stok Habis
          </Button>
        ) : (
          <Stack
            w={{ base: 'fit-content', lg: 'auto' }}
            spacing={{ base: '0', lg: 2 }}
          >
            <Box
              fontSize={{ base: '12px', md: '12px' }}
              textAlign="center"
              textColor="gray.75"
              display={{ base: 'none', lg: 'block' }}
            >
              Jumlah Pembelian
            </Box>
            <QuantityControl
              key={`quantity-${item.id}`}
              variant="cart"
              inputDisabled={isProductEmpty}
              decDisabled={isProductEmpty}
              incDisabled={
                isProductEmpty || item.quantity >= item.product.stock
              }
              hasFractionalStep={fractionalStep}
              inputProps={inputProps}
              incProps={incProps}
              decProps={decProps}
            />
            <Box
              fontSize={{ base: '12px', md: '12px' }}
              textAlign="center"
              textColor="gray.75"
              display={{ base: 'none', lg: 'block' }}
            >
              {nameUnit}
            </Box>
          </Stack>
        )}
      </Flex>
      {isProductEmpty ? (
        <Link
          href={`/product/${item.product.id}/suggestions?cat1=&page=1`}
          passHref
          css={{
            width: '100%',
          }}
        >
          <Button
            w="full"
            h={{ base: '32px', lg: '40px' }}
            variant="outline"
            background="white"
            colorScheme="blue"
            size="sm"
            fontSize="14px"
            rightIcon={<ArrowRightIcon />}
            onClick={() =>
              trackEvent(
                TRACKING_EVENTS.CLICK_PRODUCT_RELATED,
                getItemCardTrackingParams(
                  item.product,
                  (item.product?.minimum_order_quantity ?? 1000) / 1000,
                  'cart',
                  'cart',
                  'cart-out-of-stock'
                )
              )
            }
          >
            Produk Serupa
          </Button>
        </Link>
      ) : null}
    </VStack>
  );
};

interface CartItemProps {
  item: Cart;
  onToggle: (id: string, currentQuantity?: number) => void;
  isSelected: boolean;
  isLoading?: boolean;
}

const CartItemComponent = ({
  item,
  onToggle,
  isSelected,
  isLoading,
}: CartItemProps) => {
  const [currentInputQuantity, setCurrentInputQuantity] = useState<
    number | undefined
  >(
    item.quantity // Initialize with item quantity from cache
  );
  const currentInputQuantityRef = useRef<number | undefined>(item.quantity);
  const latestQuantityRefFromActions =
    useRef<React.MutableRefObject<number> | null>(null);
  const toast = useToast();
  const updateCartItemQuantity = useCartStore((s) => s.updateCartItemQuantity);
  const isProductEmpty = item.product.stock === 0;

  // Sync currentInputQuantity when item.quantity changes from external updates (e.g., add-to-cart)
  useEffect(() => {
    if (currentInputQuantity !== item.quantity) {
      setCurrentInputQuantity(item.quantity);
      currentInputQuantityRef.current = item.quantity;
    }
  }, [item.quantity, item.id]);

  const promo = useCartItemPromoPrice(item, isSelected);

  const getCurrentQuantityMagnifiedForPatch = useCallback(() => {
    const latestFromActions = latestQuantityRefFromActions.current?.current;
    return (
      latestFromActions ?? currentInputQuantityRef.current ?? item.quantity
    );
  }, [item.quantity]);

  const notify = useCallback(
    (type: 'success' | 'error', message: string) => {
      showToast(toast, type, message);
    },
    [toast]
  );

  const {
    isNoteFieldVisible,
    currentNote,
    savedNote,
    openNoteEditor,
    cancelNoteEdit,
    confirmNoteEdit,
    noteInputProps,
    charCount,
  } = useCartItemNote({
    item,
    isDisabled: isProductEmpty,
    getCurrentQuantityMagnified: getCurrentQuantityMagnifiedForPatch,
    onValidationError: (msg) => notify('error', msg),
  });

  const handleQuantityChange = useCallback((magnifiedQuantity: number) => {
    currentInputQuantityRef.current = magnifiedQuantity;
    setCurrentInputQuantity((prev) => {
      if (prev === magnifiedQuantity) {
        return prev;
      }
      return magnifiedQuantity;
    });
  }, []);

  const handleToggle = useCallback(() => {
    if (!isSelected) {
      const latestQuantityFromRef =
        latestQuantityRefFromActions.current?.current;
      const latestQuantity =
        latestQuantityFromRef ?? currentInputQuantity ?? item.quantity;

      updateCartItemQuantity(item.id, latestQuantity);

      onToggle(item.id, latestQuantity);
    } else {
      const latestQuantity = currentInputQuantity ?? item.quantity;
      onToggle(item.id, latestQuantity);
    }
  }, [
    item.id,
    item.quantity,
    currentInputQuantity,
    isSelected,
    onToggle,
    updateCartItemQuantity,
  ]);

  const { handleDeleteItem, isDeleting } = useCartDeleteItem({
    itemId: item.id,
    item,
    notify,
  });

  return (
    <HStack
      spacing={{ base: 2, lg: 4 }}
      py={{ base: 3, lg: 4 }}
      className="card-cart"
      data-item-code={item.product.item_code}
    >
      <Flex
        alignSelf="start"
        minH={{ base: 'auto', lg: '144px' }}
        alignItems="start"
      >
        <Checkbox
          isChecked={isSelected && item.product.stock !== 0}
          isDisabled={isProductEmpty}
          onChange={handleToggle}
          size={{ base: 'md', lg: 'lg' }}
          colorScheme="blue"
          mt="4px"
          sx={{
            '& .chakra-checkbox__control': {
              bg: (() => {
                if (isProductEmpty) return '#F1F1F1';
                if (isSelected && item.product.stock !== 0) return 'blue.500';
                return 'white';
              })(),
              borderColor: (() => {
                if (isProductEmpty) return '#F1F1F1';
                if (isSelected && item.product.stock !== 0) return 'blue.500';
                return '#8E8E93';
              })(),
              _checked: {
                bg: 'blue.500',
                borderColor: 'blue.500',
                color: 'white',
                _hover: {
                  bg: 'blue.600',
                  borderColor: 'blue.600',
                },
              },
            },
          }}
        />
      </Flex>
      <Stack w="100%" spacing={{ base: 2, lg: 4 }} id="cart-item-list">
        <Box
          border={{ base: 'none', lg: '1px solid #D9D9D9' }}
          borderRadius={{ base: '0', lg: '8px' }}
          minH={{ base: 'auto', lg: '144px' }}
          bgColor={isProductEmpty ? '#F1F1F1' : 'white'}
          rounded="md"
          {...(isProductEmpty
            ? { padding: '8px' }
            : {
                px: { base: 0, lg: 4 },
                pb: { base: 0, lg: 4 },
                pt: { base: 0, lg: 4 },
              })}
        >
          {/* Mobile Layout */}
          <VStack
            spacing={1}
            w="full"
            alignItems="flex-start"
            justifyContent="flex-start"
            display={{ base: 'flex', lg: 'none' }}
          >
            <HStack alignItems="flex-start" w="full">
              <CartItemImage
                imageUrl={item.product.product_images_medium?.[0]}
                alt={item.product.title}
                productId={item.product.id}
                item={item}
                size={{ base: '64px', md: '64px' }}
              />
              <VStack alignItems="flex-start" id="cart-item-info" w="full">
                <Box flex="1">
                  <CartItemInfo
                    item={item}
                    noteProps={{
                      isNoteFieldVisible,
                      openNoteEditor,
                      cancelNoteEdit,
                      confirmNoteEdit,
                      savedNote,
                      currentNote,
                      noteInputProps,
                      charCount,
                    }}
                  />
                </Box>
                {/* Bottom Row: Price + Quantity + Delete */}
                <HStack
                  spacing={{ base: '3px', lg: 2 }}
                  justifyContent="space-between"
                  w="full"
                  alignItems={{ base: 'flex-start' }}
                >
                  <CartItemPromoPrice
                    item={item}
                    promo={promo}
                    isSelected={isSelected}
                    isLoading={isLoading}
                  />
                  <CartItemActions
                    item={item}
                    notify={notify}
                    onQuantityChange={handleQuantityChange}
                    isSelected={isSelected}
                    onLatestQuantityRefReady={(ref) => {
                      latestQuantityRefFromActions.current = ref;
                    }}
                    noteForPatch={savedNote}
                  />
                </HStack>
              </VStack>
            </HStack>
            <HStack
              justifyContent="space-between"
              w="full"
              alignItems="flex-start"
            >
              <Box flex="1" minWidth="0">
                {!isNoteFieldVisible ? (
                  <CartItemNoteToggle
                    isDisabled={isProductEmpty}
                    onToggle={openNoteEditor}
                    note={savedNote}
                    display={{ base: 'flex', lg: 'none' }}
                  />
                ) : null}
                {isNoteFieldVisible ? (
                  <CartItemNoteEditor
                    noteInputProps={noteInputProps}
                    charCount={charCount}
                    onCancel={cancelNoteEdit}
                    onConfirm={confirmNoteEdit}
                    display={{ base: 'flex', lg: 'none' }}
                  />
                ) : null}
              </Box>
              <IconButton
                name="remove-item"
                icon={<DeleteIcon />}
                aria-label="remove button"
                bg="#FAFAFA"
                color="red"
                size="sm"
                _active={{ bgColor: 'white' }}
                _hover={{ bgColor: 'gray.33' }}
                onClick={handleDeleteItem}
                isLoading={isDeleting}
                display={{ base: 'flex', lg: 'none' }}
                flexShrink={0}
              />
            </HStack>
          </VStack>

          {/* Desktop Layout */}
          <Flex
            justifyContent="space-between"
            w="full"
            display={{ base: 'none', lg: 'flex' }}
          >
            <CartItemImage
              imageUrl={item.product.product_images_medium?.[0]}
              alt={item.product.title}
              productId={item.product.id}
              item={item}
              size={{ base: '64px', md: '112px' }}
            />
            <Flex justifyContent="space-between" w="full" ml="4">
              <CartItemInfo
                item={item}
                noteProps={{
                  isNoteFieldVisible,
                  openNoteEditor,
                  cancelNoteEdit,
                  confirmNoteEdit,
                  savedNote,
                  currentNote,
                  noteInputProps,
                  charCount,
                }}
              />
              <CartItemPromoPrice
                item={item}
                promo={promo}
                isSelected={isSelected}
                isLoading={isLoading}
              />
              <CartItemActions
                item={item}
                notify={notify}
                onQuantityChange={handleQuantityChange}
                isSelected={isSelected}
                onLatestQuantityRefReady={(ref) => {
                  latestQuantityRefFromActions.current = ref;
                }}
                noteForPatch={savedNote}
              />
            </Flex>
          </Flex>
        </Box>
      </Stack>
    </HStack>
  );
};

export const CartItem = React.memo(CartItemComponent);
